import React, { useEffect, useState, useRef } from "react";
import { FloatingLabelInput } from "../floatinglable/floating";
import { useDebounce } from "@/hooks/use-debounce";

type BaseSearchCriteria = {
  limit: number;
  search_key?: string;
  offset: number;
  sort_key: string;
  sort_order: string;
  [key: string]: any;
};

type TableSearchFilterProps<T extends BaseSearchCriteria = BaseSearchCriteria> = {
  setSearchCriteria: React.Dispatch<React.SetStateAction<T>>;
  placeHolder?: string;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  inputValue: string;
  disable?: boolean;
  initialValue?: Partial<T>;
};

const TableSearchFilter = <T extends BaseSearchCriteria = BaseSearchCriteria>({
  setSearchCriteria,
  placeHolder = "Search by Name",
  inputValue = "",
  setInputValue,
  initialValue,
  disable = false,
}: TableSearchFilterProps<T>) => {
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const debouncedInputValue = useDebounce<string>(inputValue, 500);

  // Bahar click hone par band karo
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setMobileSearchOpen(false);
        setInputValue("");
      }
    };
    if (mobileSearchOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mobileSearchOpen]);

  useEffect(() => {
    setSearchCriteria((prev) => {
      const newCriteria = { ...prev } as T;
      if (debouncedInputValue.trim() !== "") {
        newCriteria.search_key = debouncedInputValue.trim();
        newCriteria.offset = 0;
        newCriteria.sort_key = initialValue?.sort_key ?? prev.sort_key ?? "id";
        newCriteria.sort_order = initialValue?.sort_order ?? prev.sort_order ?? "desc";
      } else {
        delete newCriteria.search_key;
      }
      return newCriteria;
    });
  }, [debouncedInputValue, setSearchCriteria, initialValue]);

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
    setInputValue(event.target.value);
  };

  const preventLeadingSpace: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
    if (event.key === " " && !(event.target as HTMLInputElement).value) {
      event.preventDefault();
    }
  };

  return (
    <div className="flex items-center flex-1 space-x-2 mb-2 lg:mb-0">

      {/* Desktop */}
      <div className="hidden lg:flex items-center relative w-full lg:w-auto">
        <i className="fa-solid fa-magnifying-glass size-4 text-gray-400 absolute left-1 z-10 ml-2"></i>
        <FloatingLabelInput
          id="search"
          placeholder={placeHolder}
          value={inputValue}
          disabled={disable}
          onChange={handleInputChange}
          onKeyDown={preventLeadingSpace}
          className="w-80 lg:w-64 pl-8 text-sm placeholder:text-sm text-gray-400 h-8"
        />
      </div>

      {/* Mobile */}
      <div ref={searchRef} className="flex lg:hidden items-center">
        {!mobileSearchOpen ? (
          <button
            onClick={() => setMobileSearchOpen(true)}
            className="flex items-center justify-center w-8 h-8 rounded-full border border-gray-300"
          >
            <i className="fa-solid fa-magnifying-glass text-gray-400 text-sm"></i>
          </button>
        ) : (
          <div className="flex items-center relative">
            <i className="fa-solid fa-magnifying-glass size-4 text-gray-400 absolute left-3 z-10"></i>
            <FloatingLabelInput
              id="search-mobile"
              placeholder={placeHolder}
              value={inputValue}
              disabled={disable}
              onChange={handleInputChange}
              onKeyDown={preventLeadingSpace}
              autoFocus
              className="w-36 pl-8 text-sm placeholder:text-sm text-gray-400 h-8"
            />
          </div>
        )}
      </div>

    </div>
  );
};

export default TableSearchFilter;